import { sameSquare } from './board';
import { isFace } from './cards';
import { isActionShape, isSide, parseTarget, parseWild } from './actions';
import { curioError, useCurio } from './ledger/curios';
import { FREE_COLLATERAL, staysAsCollateral } from './ledger/flow';
import { holds, maxLinks } from './ledger/state';
import { sameTrickTarget } from './ledger/tricks';
import { generateMoves, withoutPawn } from './movement';
import {
  afterLink,
  canRecover,
  cardMatches,
  checkEpitaphReplay,
  chooseEpitaph,
  chooseInscription,
  collateralChoices,
  concede,
  continueTurn,
  drawCard,
  endChain,
  endTurn,
  epitaphPlaysLeft,
  firstLinkColorOk,
  linkOptionsForCard,
  movablePawns,
  passSide,
  playEpitaph,
  recover,
  resolveLink,
  startRound,
  type LinkOption,
} from './rules';
import { boardCtx, cloneState } from './state';
import type {
  Action,
  ApplyResult,
  EngineError,
  ErrorCode,
  Face,
  MatchEvent,
  MatchState,
  PlayLinkAction,
} from './types';

const err = (code: ErrorCode, message: string): { error: EngineError } => ({
  error: { code, message },
});

/**
 * Validates a PLAY_LINK action and returns the fully specified link, or an error (§9.1, §16.3).
 * Does not modify the state.
 */
export function validateLink(
  s: MatchState,
  a: PlayLinkAction,
): LinkOption | { error: EngineError } {
  const side = a.side;
  const pl = s.players[side];
  if (typeof a.cardId !== 'string' || !pl.hand.includes(a.cardId))
    return err('CARD_NOT_IN_HAND', `${String(a.cardId)} is not in ${side}'s hand`);
  const card = s.cards[a.cardId];
  if (!card) return err('CARD_NOT_IN_HAND', `unknown card ${a.cardId}`);
  if (card.iou) return err('NO_MATCH', `${card.id} is an IOU and can't be played`);
  let pawnId: string;
  if (s.chain) {
    if (a.pawnId !== undefined && a.pawnId !== s.chain.pawnId)
      return err('WRONG_CHAIN_PAWN', `the chain pawn is ${s.chain.pawnId}`);
    pawnId = s.chain.pawnId;
  } else {
    if (typeof a.pawnId !== 'string')
      return err('INVALID_PAWN', 'the first link must name the chain pawn');
    const p = s.pawns[a.pawnId];
    if (!p || p.side !== side || p.status !== 'onBoard')
      return err('INVALID_PAWN', `${a.pawnId} is not one of ${side}'s pawns on the board`);
    if (s.ledger && !movablePawns(s, side).some((x) => x.id === p.id))
      return err('INVALID_PAWN', `${a.pawnId} is Pinned and cannot move`);
    pawnId = a.pawnId;
  }
  const pawn = s.pawns[pawnId];
  if (!pawn) return err('INVALID_PAWN', `unknown pawn ${pawnId}`);
  // Long Night: a Trick names what it is played on, and moves nothing.
  if (card.trick) {
    if (a.wild !== undefined) return err('INVALID_ACTION', 'a Trick names no Wild');
    const t = parseTarget(a.target);
    if (!t || t.kind !== 'trick')
      return err('ILLEGAL_MOVE', 'a Trick is played with { trick: { pawnId?, face? } }');
    if (!cardMatches(s, side, card, card.face as Face))
      return err('NO_MATCH', `${card.id} does not match the top`);
    const found = linkOptionsForCard(s, side, card.id, pawnId).find(
      (o) => o.trick && sameTrickTarget(o.trick.target, t.trick),
    );
    return found ?? err('INVALID_TARGET', `the ${card.trick} cannot be played that way now`);
  }
  let face: Face;
  let collateralPawnId: string | null = null;
  let ctx = boardCtx(s);
  if (card.kind === 'normal') {
    if (a.wild !== undefined)
      return err('INVALID_ACTION', 'only a Wild names a face and collateral');
    face = card.face as Face;
    if (!cardMatches(s, side, card, face))
      return err('NO_MATCH', `${card.id} does not match the top`);
    if (!firstLinkColorOk(s, side, face))
      return err('NO_MATCH', `Single File: the chain must begin with this round's first color`);
  } else {
    const raw = a.wild as { face?: unknown; collateralPawnId?: unknown } | null | undefined;
    if (typeof raw !== 'object' || raw === null || !isFace(raw.face))
      return err('INVALID_WILD_FACE', 'a Wild must name a face: a color and a number from 1 to 3');
    const wild = parseWild(raw);
    if (!wild) return err('INVALID_COLLATERAL', 'a Wild needs a collateral pawn');
    face = wild.face;
    if (!firstLinkColorOk(s, side, face))
      return err(
        'INVALID_WILD_FACE',
        `Single File: the chain must begin with this round's first color`,
      );
    const { onBoard, reserves, free } = collateralChoices(s, side, pawnId);
    const isFree = free && wild.collateralPawnId === FREE_COLLATERAL;
    if (
      !isFree &&
      !onBoard.includes(wild.collateralPawnId) &&
      !reserves.includes(wild.collateralPawnId)
    )
      return err('INVALID_COLLATERAL', `${wild.collateralPawnId} cannot be pawned for this Wild`);
    collateralPawnId = wild.collateralPawnId;
    if (!isFree && !staysAsCollateral(s, s.pawns[collateralPawnId]))
      ctx = withoutPawn(ctx, collateralPawnId);
  }
  const target = parseTarget(a.target);
  if (!target) return err('ILLEGAL_MOVE', 'the target must be { to: square } or { hops: squares }');
  if (target.kind === 'trick') return err('ILLEGAL_MOVE', 'only a Trick is played on { trick }');
  const moves = generateMoves(ctx, pawn, face);
  // Long Night: a Knight's Gait pawn lists every jump of a Green card, as Yellow lists hops.
  const listsSteps = (m: (typeof moves)[number]) =>
    m.move.type === 'hops' || m.move.type === 'knights';
  const expectsHops = face.color === 'Y' || (face.color === 'G' && moves.some(listsSteps));
  if ((target.kind === 'hops') !== expectsHops)
    return err(
      'ILLEGAL_MOVE',
      expectsHops ? 'Yellow moves list every hop' : 'only Yellow moves list hops',
    );
  const option = moves.find((m) =>
    target.kind === 'to'
      ? m.landings.length === 1 && !listsSteps(m) && sameSquare(m.landings[0], target.to)
      : listsSteps(m) &&
        m.landings.length === target.hops.length &&
        m.landings.every((l, i) => sameSquare(l, target.hops[i])),
  );
  if (!option) return err('ILLEGAL_MOVE', 'that move is not legal for this pawn and face');
  return { cardId: card.id, pawnId, face, wasWild: card.kind === 'wild', collateralPawnId, option };
}

/**
 * Applies an action (§16.1). Returns the new state and its events, or an error; an illegal action
 * never changes the input state (§16.3).
 */
export function applyAction(state: MatchState, action: Action): ApplyResult {
  if (!isActionShape(action)) return err('INVALID_ACTION', 'unknown action type or side');
  if (state.phase === 'MATCH_OVER') return err('MATCH_OVER', 'the match is over');
  const side = action.side;
  const events: MatchEvent[] = [];

  if (action.type === 'CONCEDE') {
    const s = cloneState(state);
    s.seq += 1;
    concede(s, side, events);
    return { state: s, events };
  }
  if (side !== state.decider) return err('NOT_YOUR_DECISION', `${state.decider} must act now`);
  if (action.type === 'USE_CURIO') {
    if (!state.ledger) return err('NO_CURIO', 'Curios exist only at the Long Night');
    const problem = curioError(state, action);
    if (problem) return err(problem.includes('holds no') ? 'NO_CURIO' : 'INVALID_TARGET', problem);
    const s = cloneState(state);
    s.seq += 1;
    const used = useCurio(s, action, events, (st, sd) => drawCard(st, sd, 'draw', events));
    if (used.kind === 'replay')
      playEpitaph(s, side, used.epitaph, used.pawnId, events, { fromSlot: false });
    return { state: s, events };
  }

  const wrongPhase = () => err('WRONG_PHASE', `${action.type} is not allowed in ${state.phase}`);
  const run = (fn: (s: MatchState) => void): ApplyResult => {
    const s = cloneState(state);
    s.seq += 1;
    fn(s);
    return { state: s, events };
  };

  switch (state.phase) {
    case 'CHOOSE_STARTER': {
      if (action.type !== 'CHOOSE_STARTER') return wrongPhase();
      if (!isSide(action.starter)) return err('INVALID_CHOICE', 'the starter must be S or N');
      return run((s) => startRound(s, action.starter, 'loserChoice', side, events));
    }
    case 'TURN_START': {
      if (action.type === 'PASS')
        return run((s) => {
          passSide(s, side, 'voluntary', events);
          endTurn(s, events);
        });
      if (action.type === 'CONTINUE') return run((s) => continueTurn(s, side, events));
      return wrongPhase();
    }
    case 'ACTION':
    case 'CHAIN': {
      if (action.type === 'PASS')
        return err('PASS_NOT_ALLOWED', 'you can only pass at the start of your turn');
      if (action.type === 'PLAY_LINK') {
        if (state.chain && state.chain.links.length >= maxLinks(state, side))
          return err('CHAIN_FULL', 'the chain already has the maximum number of links');
        const lo = validateLink(state, action);
        if ('error' in lo) return lo;
        return run((s) => {
          resolveLink(s, side, lo, events);
          afterLink(s, side, events);
        });
      }
      if (state.phase === 'CHAIN') {
        if (action.type === 'END_CHAIN') return run((s) => endChain(s, events));
        return wrongPhase();
      }
      if (action.type === 'RECOVER') {
        if (!canRecover(state, side))
          return err('RECOVER_UNAVAILABLE', 'Recover needs a card in hand and a card in the deck');
        if (typeof action.cardId !== 'string' || !state.players[side].hand.includes(action.cardId))
          return err('CARD_NOT_IN_HAND', `${String(action.cardId)} is not in ${side}'s hand`);
        return run((s) => recover(s, side, action.cardId, events));
      }
      if (action.type === 'PLAY_EPITAPH') {
        const held = state.players[side].epitaphs;
        if (!epitaphPlaysLeft(state, side))
          return err('EPITAPH_ALREADY_PLAYED', 'you already played an Epitaph this round');
        const epitaph =
          action.epitaphId === undefined
            ? held.length === 1
              ? held[0]
              : undefined
            : held.find((e) => e.id === action.epitaphId);
        if (!epitaph)
          return err(
            'NO_EPITAPH',
            held.length === 0 ? 'you hold no Epitaph' : 'name the Epitaph to play',
          );
        const pawn = typeof action.pawnId === 'string' ? state.pawns[action.pawnId] : undefined;
        if (!pawn || pawn.side !== side || pawn.status !== 'onBoard')
          return err(
            'INVALID_PAWN',
            `${String(action.pawnId)} is not one of ${side}'s pawns on the board`,
          );
        const check = checkEpitaphReplay(state, side, epitaph, pawn.id);
        if (!check.ok)
          return err(
            'EPITAPH_ROUTE_ILLEGAL',
            `link ${check.failedAt + 1} of the route cannot be replayed (${check.reason})`,
          );
        if (action.chain === true && !(state.ledger && holds(state, side, 'seance-table')))
          return err('INVALID_ACTION', 'only the Séance Table lets an Epitaph open a chain');
        const chain = action.chain === true;
        return run((s) => playEpitaph(s, side, epitaph, pawn.id, events, { chain }));
      }
      return wrongPhase();
    }
    case 'CHOOSE_INSCRIPTION': {
      if (action.type === 'PLAY_LINK' && state.pendingInscription) {
        if (state.pendingInscription.inscription.links.length >= maxLinks(state, side))
          return err('CHAIN_FULL', 'the chain has ended with the maximum number of links');
      }
      if (action.type !== 'CHOOSE_INSCRIPTION') return wrongPhase();
      if (typeof action.replace !== 'boolean')
        return err('INVALID_CHOICE', 'replace must be true or false');
      return run((s) => chooseInscription(s, action.replace, events));
    }
    case 'CHOOSE_EPITAPH': {
      if (action.type !== 'CHOOSE_EPITAPH') return wrongPhase();
      const choice = state.pendingEpitaphChoice;
      if (
        !choice ||
        typeof action.keepEpitaphId !== 'string' ||
        !choice.candidates.some((e) => e.id === action.keepEpitaphId) ||
        choice.kept.includes(action.keepEpitaphId)
      )
        return err('INVALID_CHOICE', 'choose one of the offered Epitaphs');
      return run((s) => chooseEpitaph(s, action.keepEpitaphId, events));
    }
    default:
      return wrongPhase();
  }
}
